import { useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowsInSimple,
  ArrowsOutSimple,
  Copy,
  FileArrowDown,
  Info,
  X,
} from '@phosphor-icons/react';
import { useAuthStore } from '../../../store/storeAuth';
import { onDownloadFile } from '../../../util/fileDownloader';

interface AssetPreviewModalContentProps {
  isFullscreen: boolean;
  isDarkMode: boolean;
}

interface AssetPreviewModalProps {
  open: boolean;
  onClose: () => void;
  name: string;
  provider: string;
  providerLabels: Record<string, string>;
  model?: string;
  fileType?: string;
  createdAt: string;
  assetUrl: string;
  downloadFileName: string;
  fullscreenEnabled?: boolean;
  maxWidth?: 'sm' | 'md';
  contentMinHeight?: string;
  renderContent: (props: AssetPreviewModalContentProps) => React.ReactNode;
}

function AssetPreviewModal({
  open,
  onClose,
  name,
  provider,
  providerLabels,
  model,
  fileType,
  createdAt,
  assetUrl,
  downloadFileName,
  fullscreenEnabled = false,
  maxWidth = 'md',
  contentMinHeight = '200px',
  renderContent,
}: AssetPreviewModalProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const isDarkMode = useAuthStore(s => s.user?.theme === 'dark');

  if (!open) return null;

  const displayName = name || 'Untitled';
  const displayProvider = providerLabels[provider] ?? provider;
  const formattedDate = new Date(createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(assetUrl);
  };

  const handleDownload = () => {
    onDownloadFile(assetUrl, downloadFileName);
  };

  const handleClose = () => {
    setIsFullscreen(false);
    onClose();
  };

  const iconColor = isDarkMode ? '#D0D5DD' : '#667085';

  return (
    <Dialog
      open
      onClose={handleClose}
      fullScreen={fullscreenEnabled && isFullscreen}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullscreenEnabled && isFullscreen ? 0 : '12px',
          overflow: 'hidden',
          backgroundColor: isDarkMode ? '#1D2939' : '#FFF',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: isDarkMode ? '#F2F4F7' : '#344054',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '60%',
          }}
        >
          {displayName}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Tooltip title={showInfo ? 'Hide info' : 'Show info'}>
            <IconButton onClick={() => setShowInfo(!showInfo)} size='small'>
              <Info
                size={18}
                color={iconColor}
                weight={showInfo ? 'fill' : 'regular'}
              />
            </IconButton>
          </Tooltip>
          {fullscreenEnabled && (
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <IconButton
                onClick={() => setIsFullscreen(!isFullscreen)}
                size='small'
              >
                {isFullscreen ? (
                  <ArrowsInSimple size={18} color={iconColor} />
                ) : (
                  <ArrowsOutSimple size={18} color={iconColor} />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Download'>
            <IconButton onClick={handleDownload} size='small'>
              <FileArrowDown size={18} color={iconColor} />
            </IconButton>
          </Tooltip>
          <Tooltip title='Copy URL'>
            <IconButton onClick={handleCopy} size='small'>
              <Copy size={18} color={iconColor} />
            </IconButton>
          </Tooltip>
          <Tooltip title='Close'>
            <IconButton onClick={handleClose} size='small'>
              <X size={18} color={iconColor} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showInfo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            pb: 1.5,
          }}
        >
          <Chip
            label={displayProvider}
            size='small'
            sx={{
              height: '22px',
              fontSize: '11px',
              fontWeight: 500,
              backgroundColor: isDarkMode ? '#101828' : '#EEF4FF',
              color: isDarkMode ? '#84ADFF' : '#3650DE',
            }}
          />
          {fileType && (
            <Chip
              label={fileType.toUpperCase()}
              size='small'
              sx={{
                height: '22px',
                fontSize: '11px',
                fontWeight: 500,
                backgroundColor: isDarkMode ? '#101828' : '#F2F4F7',
                color: isDarkMode ? '#D0D5DD' : '#475467',
              }}
            />
          )}
          {model && (
            <>
              <Typography
                sx={{ fontSize: '12px', color: isDarkMode ? '#667085' : '#98A2B3' }}
              >
                {model}
              </Typography>
              <Box
                sx={{
                  width: '3px',
                  height: '3px',
                  borderRadius: '50%',
                  backgroundColor: isDarkMode ? '#475467' : '#D0D5DD',
                }}
              />
            </>
          )}
          <Typography
            sx={{ fontSize: '12px', color: isDarkMode ? '#667085' : '#98A2B3' }}
          >
            {formattedDate}
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: isDarkMode ? '#344054' : undefined }} />

      <DialogContent
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: isDarkMode ? '#101828' : '#F9FAFB',
          minHeight: contentMinHeight,
        }}
      >
        {renderContent({ isFullscreen, isDarkMode: !!isDarkMode })}
      </DialogContent>
    </Dialog>
  );
}

export { AssetPreviewModal };
export type { AssetPreviewModalContentProps };
